import { useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router'
import { AccountMenu } from '@/auth/AccountMenu'
import { useAuth } from '@/auth/AuthProvider'
import { ImpersonationBanner } from '@/auth/ImpersonationBanner'
import { useMode } from '@/app/mode'
import { useGlobalSaveStatus } from '@/data/hooks'
import { getPreviewReturn, setPreviewReturn } from '@/staff/previewReturn'
import { Icons, SaveState, type SaveStatus } from '@/ui'

/** `section` is the last breadcrumb for staff in prep/preview ("Onboarding", "People"); clients just see the client name. */
export function TopBar({ clientName, section, saveStatus, homeHref }: { clientName?: string; section?: string; saveStatus?: SaveStatus; homeHref: string }) {
  const { role } = useAuth()
  const { clientId } = useParams()
  const mode = useMode()
  const globalStatus = useGlobalSaveStatus()
  const shown: SaveStatus = saveStatus ?? globalStatus
  const ref = useRef<HTMLElement>(null)
  // Publish the header's live height (it grows when the team banner shows) so sticky panels sit below it.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--topbar-h', `${el.offsetHeight}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty('--topbar-h')
    }
  }, [])
  return (
    <header ref={ref} className="sticky top-0 z-30 border-b border-gray-200 bg-paper/95 backdrop-blur">
      <ImpersonationBanner />
      {mode !== 'client' && (
        <div className="bg-eerie px-4 py-1.5 text-center text-[0.6875rem] uppercase tracking-eyebrow text-paper">
          {mode === 'prep' ? (
            'Internal team view · Fill in what you know. The client confirms it.'
          ) : (
            <>
              Previewing as a client: nothing is saved. Try anything.
              {role === 'staff' && (() => {
                const back = getPreviewReturn()
                return (
                  <Link to={back ?? (clientId ? `/staff/clients/${clientId}` : '/staff')} onClick={() => setPreviewReturn(null)} className="ml-3 inline-block py-1 underline underline-offset-4">
                    Exit preview
                  </Link>
                )
              })()}
            </>
          )}
        </div>
      )}
      <div className="mx-auto flex max-w-portal items-center gap-4 px-6 py-3 md:px-10">
        {/* Phones have no room for the breadcrumb, so prefilling gets the usual back arrow to the client's page instead. */}
        {mode === 'prep' && role === 'staff' && clientId && (
          <Link
            to={`/staff/clients/${clientId}`}
            aria-label={`Back to ${clientName ?? 'the client'}`}
            className="-ml-2 -mr-2 grid h-10 w-10 shrink-0 place-items-center text-ink hover:bg-gray-100 sm:hidden"
          >
            <Icons.ArrowLeft size={18} />
          </Link>
        )}
        <Link to={homeHref} className="shrink-0" aria-label="Portal home">
          <img src="/brand/sonder-logo.png" alt="Sonder Media House" width={105} height={32} />
        </Link>
        {/* Breadcrumb only while prefilling; preview shows exactly what the client sees. */}
        {mode === 'prep' && role === 'staff' && clientId ? (
          <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center gap-2 text-small text-dim sm:flex">
            <Link to="/staff" className="underline-offset-4 hover:text-ink hover:underline">Clients</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/staff/clients/${clientId}`} className="truncate underline-offset-4 hover:text-ink hover:underline">{clientName ?? clientId}</Link>
            {section && (
              <>
                <span aria-hidden="true">/</span>
                <span className="text-ink">{section}</span>
              </>
            )}
          </nav>
        ) : (
          clientName && <span className="hidden text-small text-dim sm:inline">· {clientName}</span>
        )}
        <div className="ml-auto flex items-center gap-4">
          <SaveState status={shown} />
          <AccountMenu tone="light" />
        </div>
      </div>
    </header>
  )
}
